import { initTheme, Toaster, TooltipProvider } from '@enp/ui';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createStationData } from './data/create-station-data.js';
import { provideStationData } from './data/station-data-store.js';
import { startAppUpdatePrompt } from './device/app-update.js';
import { BridgeProvider } from './device/bridge-context.js';
import { selectBridge } from './device/select-bridge.js';
import { createAppRouter } from './router.js';
import { pruneAwaiting, watchAwaitingFigures } from './stock/awaiting-figures.js';
import { watchCountMovements } from './stock-take/draft-store.js';
import './app.css';

initTheme();

const root = document.getElementById('root');
if (!root) throw new Error('#root is missing from index.html');

const bridge = await selectBridge();

// The data layer starts in the background: the station renders at once, and a local
// database that can't open shows as a sync problem instead of a blank screen.
const stationData = await createStationData().catch((err: unknown) => {
  console.error('Station data layer unavailable', err);
  return null;
});
provideStationData(stationData);
if (stationData) {
  stationData.start().catch((err: unknown) => console.error('Station data layer failed', err));
  startAppUpdatePrompt(stationData);
  watchCountMovements(stationData);
  watchAwaitingFigures(stationData);
  if (import.meta.env.DEV) {
    // For e2e tests and debugging in development builds only.
    (window as unknown as { enpStationData?: unknown }).enpStationData = stationData;
    // The mocked e2e runs have no download stream: this stands in for a download arriving
    // now (what the office answered is then in the figures).
    (window as unknown as { enpDownloaded?: () => Promise<void> }).enpDownloaded = () =>
      pruneAwaiting(stationData, new Date());
  }
}
const { router, queryClient } = createAppRouter(bridge);

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BridgeProvider bridge={bridge}>
        <TooltipProvider delayDuration={300}>
          <RouterProvider router={router} />
          {/* Bottom centre: the top holds the sync pill and the offline and refusal banners,
              which a toast must never cover (UI7S-9); the bottom-right corner belongs to the
              primary action. */}
          <Toaster position="bottom-center" />
        </TooltipProvider>
      </BridgeProvider>
    </QueryClientProvider>
  </StrictMode>,
);
